import {
  isComplianceTracked, lifecycleStateSchema, orderedLanguages, personLanguagesSchema, updatePersonSchema, type ChecklistItem, type HrWorkflowView, type Person, type PersonChecklist, type PersonFilesResponse,
  type LanguageId, type PersonLanguages, type PersonTimelinePage, type UpdatePerson,
} from '@vigeo/shared';
import { detailFields, detailPatchOf, detailValuesOf, type DetailField } from '@/features/people';
import { lifecyclePresenters, statusDetailLine } from '@/features/candidates';
import { entryTitle } from '@/features/timeline';

/**
 * What the employee profile (#585) shows and edits, as plain functions: which fields make up each group, and every
 * line the page works out from records it reads. Nothing here is stored, and nothing here asks the server anything.
 */

export const PROFILE_TABS = ['overview', 'personal', 'employment', 'compliance', 'documents', 'notes'] as const;
export type ProfileTab = (typeof PROFILE_TABS)[number];
export const tabLabels: Record<ProfileTab, string> = {
  overview: 'Overview', personal: 'Personal', employment: 'Employment', compliance: 'Compliance', documents: 'Documents', notes: 'Notes and history',
};
/** The two tabs that read tracking and files: not drawn where neither can hold anything (#723, `complianceAndDocumentsShown`). */
const TRACKING_TABS: readonly ProfileTab[] = ['compliance', 'documents'];
/** The tabs drawn, in order. `withTracking` false leaves Compliance and Documents out. */
export function tabsShown(withTracking: boolean): readonly ProfileTab[] {
  return withTracking ? PROFILE_TABS : PROFILE_TABS.filter((tab) => !TRACKING_TABS.includes(tab));
}
/** The tab a `?tab=` value names; anything else, and a tab that is not drawn, is the Overview. */
export function tabOf(value: string | null, withTracking = true): ProfileTab {
  return (tabsShown(withTracking) as readonly string[]).includes(value ?? '') ? (value as ProfileTab) : 'overview';
}

export const NOT_RECORDED = 'Not recorded';

/** The groups whose Edit is a plain list of fields. Service areas and notes have editors of their own. */
export type FieldGroup = 'identity' | 'emergency' | 'employment' | 'emr';
export const groupTitles: Record<FieldGroup | 'service-area' | 'notes', string> = {
  identity: 'Identity and contact', emergency: 'Emergency contact', employment: 'Role and employment',
  'service-area': 'Service area', emr: 'System access (EMR)', notes: 'Notes and history',
};

/**
 * The fields are PICKED BY PATH from the one list the Profile step already edits (`detailFields`), so a field keeps one
 * path and one way of being written. Picking, rather than leaving out, is the point: a field added to that list later
 * (the birth date sits in it today, ADR-012, #205) does not arrive on this page by itself.
 */
function pick(source: readonly DetailField[], paths: readonly string[], labels: Readonly<Record<string, string>> = {}): readonly DetailField[] {
  return paths.map((path) => {
    const field = source.find((candidate) => candidate.path === path);
    if (field === undefined) throw new Error(`The profile names a field the person form does not have: ${path}`);
    return labels[path] === undefined ? field : { ...field, label: labels[path] };
  });
}

export const groupFields: Record<FieldGroup, readonly DetailField[]> = {
  identity: pick(detailFields.contact, [
    'name.givenName', 'name.middleName', 'name.familyName', 'name.preferredName', 'identity.previousName',
    'contact.phone', 'contact.homePhone', 'contact.alternatePhone', 'contact.email', 'contact.preferredCallTime',
    'contact.address.streetAddress', 'contact.address.addressLine2', 'contact.address.city', 'contact.address.state', 'contact.address.postalCode',
  ], { 'contact.preferredCallTime': 'Best time to call' }),
  emergency: detailFields.contact.filter((field) => field.path.startsWith('contact.emergencyContacts.')),
  employment: pick(detailFields.assignment, [
    'assignment.positionTitle', 'assignment.employmentClassification', 'assignment.employmentType', 'hireDate', 'assignment.supervisorName',
  ], { 'assignment.employmentClassification': 'Employment type', 'assignment.employmentType': 'Schedule', hireDate: 'Hire date', 'assignment.supervisorName': 'Supervisor' }),
  emr: detailFields.emr,
};

export const groupValues = (person: Person, group: FieldGroup): Record<string, string> => detailValuesOf(person, groupFields[group]);

const sameKeys = (a: readonly string[], b: readonly string[]) => a.length === b.length && [...a].sort().join('\n') === [...b].sort().join('\n');

/** The languages the Identity form edits: the ticked ids and the text for Other. A draft, so `other` is a string and not yet null. */
export interface LanguagesDraft { readonly spoken: readonly LanguageId[]; readonly other: string }

/** What the Identity form opens with. */
export const languagesDraftOf = (person: Pick<Person, 'languages'>): LanguagesDraft => ({ spoken: person.languages?.spoken ?? [], other: person.languages?.other ?? '' });

/** The draft as the schema's own value: text only while Other is ticked, blank text as none. Throws that schema's error when a value is refused. */
function languagesOf(draft: LanguagesDraft): PersonLanguages {
  const hasOther = draft.spoken.includes('other');
  return personLanguagesSchema.parse({ spoken: orderedLanguages(draft.spoken), other: hasOther && draft.other.trim() !== '' ? draft.other : null });
}

const sameLanguages = (a: LanguagesDraft, b: LanguagesDraft) =>
  sameKeys(a.spoken, b.spoken) && (a.spoken.includes('other') ? a.other.trim() === b.other.trim() : true);

/**
 * The sparse edit one group sends: only what changed, parsed by the schema the server parses it with. Throws that
 * schema's error when a value is refused. `roles`, for the employment group, is the ticked job profiles; `languages`,
 * for the identity group, is the ticked languages. Each is sent only when it moved.
 */
export function groupPatch(
  person: Person, group: FieldGroup, draft: Readonly<Record<string, string>>, roles?: readonly string[], languages?: LanguagesDraft,
): UpdatePerson {
  const patch = detailPatchOf(groupValues(person, group), draft, groupFields[group]);
  const withRoles = group === 'employment' && roles !== undefined && !sameKeys(roles, person.jobProfileKeys) ? { jobProfileKeys: [...roles] } : {};
  const withLanguages = group === 'identity' && languages !== undefined && !sameLanguages(languages, languagesDraftOf(person)) ? { languages: languagesOf(languages) } : {};
  if (Object.keys(withRoles).length === 0 && Object.keys(withLanguages).length === 0) return patch;
  return updatePersonSchema.parse({ ...patch, ...withRoles, ...withLanguages });
}

/** Whether a draft differs from what the form opened with. Typing a value and putting it back counts as nothing. */
export function groupIsDirty(
  person: Person, group: FieldGroup, draft: Readonly<Record<string, string>>, roles?: readonly string[], languages?: LanguagesDraft,
): boolean {
  const original = groupValues(person, group);
  if (groupFields[group].some(({ path }) => (draft[path] ?? '').trim() !== (original[path] ?? ''))) return true;
  if (group === 'identity' && languages !== undefined && !sameLanguages(languages, languagesDraftOf(person))) return true;
  return group === 'employment' && roles !== undefined && !sameKeys(roles, person.jobProfileKeys);
}

/** A shared-schema error as one line per field, with the label the form shows. */
export function problemsOf(error: unknown, fields: readonly DetailField[]): string {
  if (error instanceof Error && 'issues' in error && Array.isArray(error.issues)) {
    return (error.issues as ReadonlyArray<{ readonly path: ReadonlyArray<string | number>; readonly message: string }>).map((issue) => {
      const path = issue.path.join('.');
      const label = fields.find((field) => field.path === path)?.label
        ?? (path === 'spoken' || path === 'other' || path.startsWith('languages') ? 'Languages spoken' : undefined)
        ?? (path.startsWith('contact.emergencyContacts') ? 'Emergency contact (a name, relationship and phone are needed, or leave the whole contact blank)' : path);
      return `${label}: ${issue.message}`;
    }).join(' · ');
  }
  return 'Check the details and try again.';
}

const SHORT_DATE = { year: 'numeric', month: 'short', day: 'numeric' } as const;
/** A moment, as a day in the reader's own zone. */
export const dayOf = (iso: string): string => new Date(iso).toLocaleDateString('en-US', SHORT_DATE);
/**
 * A hire date is a calendar day stored at midnight UTC (`person-details-form.ts`), so it is read in UTC: in the agency's
 * zone the same instant is the evening before, and the page would show the wrong day.
 */
export const hireDay = (person: Pick<Person, 'hireDate'>): string | null =>
  person.hireDate === null ? null : new Date(person.hireDate).toLocaleDateString('en-US', { ...SHORT_DATE, timeZone: 'UTC' });

const CLASSIFICATION = { w2: 'W-2 employee', '1099': '1099 contractor' } as const;
/** "W-2 employee · Full time", or whichever half is recorded. */
export function employmentTypeLine(person: Pick<Person, 'assignment'>): string | null {
  const classification = person.assignment?.employmentClassification;
  const parts = [classification == null ? null : CLASSIFICATION[classification], person.assignment?.employmentType?.trim() || null];
  return parts.some((part) => part !== null) ? parts.filter((part) => part !== null).join(' · ') : null;
}

/** The home address on one line; null when nothing of it is recorded. */
export function homeAddressLine(person: Pick<Person, 'contact'>): string | null {
  const address = person.contact.address;
  if (address == null) return null;
  const street = [address.streetAddress, address.addressLine2].map((part) => part?.trim() ?? '').filter((part) => part !== '').join(', ');
  const stateZip = [address.state, address.postalCode].map((part) => part?.trim() ?? '').filter((part) => part !== '').join(' ');
  const line = [street, address.city?.trim() ?? '', stateZip].filter((part) => part !== '').join(', ');
  return line === '' ? null : line;
}

/** The job profile labels a person holds; a key the catalog no longer lists reads "Role", never the key. */
export function roleLabels(person: Pick<Person, 'jobProfileKeys'>, profiles: ReadonlyArray<{ readonly key: string; readonly label: string }> | undefined): readonly string[] {
  return person.jobProfileKeys.map((key) => profiles?.find((profile) => profile.key === key)?.label ?? 'Role');
}

/** Requirement statuses that are something for HR to act on. Complete, scheduled and not-applicable are not. */
const ATTENTION: ReadonlySet<ChecklistItem['status']> = new Set(['expired', 'expiring_soon', 'rejected', 'awaiting_review', 'indeterminate']);

/**
 * What "Needs attention" lists, worked out from the checklist and nothing else: every item that blocks the person's
 * gate, and every item that is expired, expiring, rejected, waiting for a review or cannot be assessed. Soonest date first.
 */
export function attentionItems(checklist: PersonChecklist): readonly ChecklistItem[] {
  const blocking = new Set(checklist.blockers.map((blocker) => blocker.requirementKey));
  return checklist.items
    .filter((item) => item.applicability !== 'does_not_apply' && (blocking.has(item.requirementKey) || ATTENTION.has(item.status)))
    .sort((a, b) => (a.dueAt ?? '9999').localeCompare(b.dueAt ?? '9999') || a.label.localeCompare(b.label));
}

/** The dated items that come due next, soonest first. An item with no date has nothing to expire. */
export function nextToExpire(checklist: PersonChecklist, limit: number): readonly ChecklistItem[] {
  return checklist.items
    .filter((item): item is ChecklistItem & { dueAt: string } => item.dueAt !== null && item.applicability !== 'does_not_apply' && item.status !== 'not_applicable')
    .sort((a, b) => a.dueAt.localeCompare(b.dueAt) || a.label.localeCompare(b.label))
    .slice(0, limit);
}

export const expiringCount = (checklist: PersonChecklist): number =>
  checklist.items.filter((item) => item.status === 'expiring_soon' || item.status === 'expired').length;

export interface NoteLine { readonly id: string; readonly text: string; readonly at: string; readonly by: string }
/** Staff notes, newest first: the workflow's `comment` records. */
export function notesOf(view: HrWorkflowView): readonly NoteLine[] {
  return view.records
    .flatMap((record) => (record.kind === 'comment' ? [{ id: record.id, text: record.text, at: record.createdAt, by: record.createdBy }] : []))
    .sort((a, b) => b.at.localeCompare(a.at));
}

/** The newest row of the person's history, as "what · when". The page is already newest first. */
export function lastChangeLine(page: PersonTimelinePage): string | null {
  const entry = page.entries[0];
  if (entry === undefined) return null;
  // A status change says why and from when it took effect (#772), beside the day it was recorded.
  const detail = entry.kind === 'event' ? statusDetailLine(entry.statusDetail) : null;
  return `${entryTitle(entry)} · ${dayOf(entry.at)}${detail === null ? '' : ` · ${detail}`}`;
}

/**
 * What the profile hands the Roster after a separation (#810): the name to say "<name> marked Separated." with. It travels in
 * the browser's own history state, never in the address, and the Roster clears it once read so a reload does not say it again.
 */
export interface SeparatedNotice { readonly separated: string }
export function separatedNoticeOf(state: unknown): string | null {
  if (typeof state !== 'object' || state === null || !('separated' in state)) return null;
  const name = (state as { separated: unknown }).separated;
  return typeof name === 'string' && name.trim() !== '' ? name : null;
}

export interface StatusChangeLine { readonly id: string; readonly change: string; readonly at: string; readonly detail: string | null }
/**
 * Each status change on the newest page of the person's history, newest first (#810): "Active → Inactive", the day it was
 * recorded, and the reason's label and the day it took effect when the change had them. Read from the history rows' codes and
 * days only; the note is on the person's record, never on a history row.
 */
export function statusChangeLines(page: PersonTimelinePage): readonly StatusChangeLine[] {
  return page.entries.flatMap((entry) => {
    if (entry.kind !== 'event' || entry.action !== 'person.lifecycle-changed') return [];
    const from = lifecycleStateSchema.safeParse(entry.from);
    const to = lifecycleStateSchema.safeParse(entry.to);
    if (!from.success || !to.success) return [];
    return [{ id: entry.id, change: `${lifecyclePresenters[from.data].label} → ${lifecyclePresenters[to.data].label}`, at: entry.at, detail: statusDetailLine(entry.statusDetail) }];
  });
}

/**
 * How this person came to be on the roster, from the record itself.
 *
 * `hiring` is whether this build has the Hiring section. Without it "Came through hiring" names a
 * screen HR does not have (#722), so the line says only when the record started.
 */
export function joinedLine(person: Pick<Person, 'staffSource' | 'createdAt' | 'intake'>, hiring = true): string {
  if (person.staffSource === 'existing-staff') return `Added by hand as existing staff · ${dayOf(person.createdAt)}`;
  return hiring ? `Came through hiring · record started ${dayOf(person.createdAt)}` : `Record started ${dayOf(person.createdAt)}`;
}

/** "12 files · newest: Signed job description". */
export function filesLine(files: PersonFilesResponse): string {
  if (files.files.length === 0) return 'No files yet';
  const newest = [...files.files].sort((a, b) => b.committedAt.localeCompare(a.committedAt))[0]!;
  return `${files.files.length} ${files.files.length === 1 ? 'file' : 'files'} · newest: ${newest.displayName}`;
}

export type UnfilledGroup = 'employment' | 'emergency' | 'address';
/** The groups "Finish this profile" lists for a staff member added by hand: the ones still empty. */
export function unfilledGroups(person: Person): readonly UnfilledGroup[] {
  const employment = person.jobProfileKeys.length === 0 && employmentTypeLine(person) === null && person.hireDate === null
    && !person.assignment?.positionTitle?.trim() && !person.assignment?.supervisorName?.trim();
  return [
    ...(employment ? ['employment' as const] : []),
    ...((person.contact.emergencyContacts ?? []).length === 0 ? ['emergency' as const] : []),
    ...(homeAddressLine(person) === null ? ['address' as const] : []),
  ];
}

export { isComplianceTracked };
